import { Award01Icon, GiftIcon } from '@hugeicons/react'
import clsx from 'clsx'

import { Card } from '@/shared/ui/Card'

import { PrizeSheetSection } from '../components/PrizeSheetSection'

type PrizeWinnerRow = {
  id?: number
  user?: { id?: number; name?: string | null } | null
}

interface DigitalProductPrizeParticipationResultProps {
  /** Ganhadores já anunciados. Um concurso pode ter vários, sorteados em
   *  rodadas — ver `design.md §7.1`. */
  winners: PrizeWinnerRow[]
  /** Identificador de quem está olhando, para marcar a própria linha. */
  currentUserId?: string | number | null
}

/**
 * Não há avatar aqui de propósito.
 *
 * `Prize::WinnerSerializer` emite identificador e nome, e mais nada: o payload
 * é servido sem autenticação e cacheado em CDN, e a redução de PII tirou dali
 * tudo que descrevia a pessoa. O `Avatar` do app sorteia uma aparência quando
 * não recebe configuração — desenharia um rosto que não é o do ganhador, e que
 * ainda mudaria a cada montagem. Medalha diz a verdade e não inventa ninguém.
 */
export const DigitalProductPrizeParticipationResult = ({
  winners,
  currentUserId
}: DigitalProductPrizeParticipationResultProps) => (
  <PrizeSheetSection
    title={winners.length > 1 ? 'Ganhadores' : 'Resultado'}
    icon={<GiftIcon size={16} variant="duotone" aria-hidden />}
  >
    <div className="flex flex-col gap-2">
      {winners.map((winner, index) => {
        const isCurrentUser =
          currentUserId != null &&
          winner.user?.id != null &&
          String(winner.user.id) === String(currentUserId)

        return (
          <Card
            key={winner.id ?? winner.user?.id ?? index}
            className={clsx(
              'shadow-sm',
              isCurrentUser &&
                'border-2 border-green-200 bg-gradient-to-br from-green-50/80 to-white'
            )}
          >
            <Card.Body className="flex items-center gap-3 p-3">
              <div
                className={clsx(
                  'flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border-2',
                  isCurrentUser
                    ? 'border-green-200 bg-green-50 text-status-success'
                    : 'border-gray-200 bg-gray-50 text-gray-500'
                )}
              >
                <Award01Icon size={22} variant="duotone" aria-hidden />
              </div>
              <div className="min-w-0 flex-1">
                <p className="m-0 flex items-center gap-1 text-caps font-bold uppercase tracking-wide text-gray-700">
                  <Award01Icon size={12} variant="stroke" aria-hidden />
                  Vencedor
                </p>
                {/* 375px é o piso: sobram 259px para esta linha depois da
                    medalha e dos respiros. Nome longo trunca, e "(você)" fica
                    fora do truncamento — é o que o participante veio ler. */}
                <p className="m-0 flex items-baseline gap-1 text-sm font-bold text-gray-900">
                  <span className="min-w-0 truncate">
                    {winner.user?.name || 'Participante'}
                  </span>
                  {isCurrentUser && (
                    <span className="shrink-0 font-semibold text-status-success">
                      (você)
                    </span>
                  )}
                </p>
              </div>
            </Card.Body>
          </Card>
        )
      })}
    </div>
  </PrizeSheetSection>
)
